import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import * as wishlistService from '../../services/wishlist.service';

export interface WishlistItem {
  id: string | number;
  title: string;
  price: number | string;
  location?: string;
  year?: number | string;
  km?: number | string;
  fuel?: string;
  img?: string;
  savedAt?: string;
  specs?: Record<string, any>;
  features?: Record<string, boolean>;
}

interface WishlistContextType {
  wishlistItems: WishlistItem[];
  addToWishlist: (item: WishlistItem) => void;
  removeFromWishlist: (id: string | number) => void;
  toggleWishlist: (item: WishlistItem) => void;
  isInWishlist: (id: string | number) => boolean;
  clearWishlist: () => void;
  refreshWishlist: (page?: number, pageSize?: number) => Promise<void>;
  wishlistCount: number;
  pagination: wishlistService.WishlistPagination | null;
  canUseWishlist: boolean;
}

const WISHLIST_STORAGE_KEY = 'morrocars_wishlist';

const isBuyer = () => Cookies.get('customerType') === '1' && Boolean(Cookies.get('token'));
const isGuest = () => !Cookies.get('token');
const canUseWishlist = () => isGuest() || isBuyer();

const getFirstImage = (images: unknown): string | undefined => {
  if (Array.isArray(images)) return images[0];
  if (typeof images === 'string') {
    try {
      const parsed = JSON.parse(images);
      return Array.isArray(parsed) ? parsed[0] : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const toWishlistItem = (vehicle: any): WishlistItem => ({
  id: vehicle.ID ?? vehicle.id,
  title: vehicle.Title || `${vehicle.MakeName || ''} ${vehicle.ModelName || ''} ${vehicle.YearOfRegistration || ''}`.trim() || 'Vehicle',
  price: vehicle.Price ?? 'Price on request',
  location: vehicle.CityName || vehicle.CityID || 'Morocco',
  year: vehicle.YearOfRegistration,
  km: vehicle.Mileage,
  fuel: vehicle.FuelTypeName || vehicle.FuelType,
  img: getFirstImage(vehicle.Images),
  savedAt: vehicle.CreatedOn ? new Date(vehicle.CreatedOn).toLocaleDateString() : undefined,
});

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>(() => {
    try {
      const saved = localStorage.getItem(WISHLIST_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error('Error reading wishlist from localStorage:', e);
      return [];
    }
  });
  const [pagination, setPagination] = useState<wishlistService.WishlistPagination | null>(null);
  const [savedVehicleIds, setSavedVehicleIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isGuest()) return;
    try {
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(wishlistItems));
    } catch (e) {
      console.error('Error saving wishlist to localStorage:', e);
    }
  }, [wishlistItems]);

  const refreshWishlist = async (page: number = 1, pageSize: number = 10) => {
    if (!isBuyer()) return;
    try {
      const res = await wishlistService.getWishlist({ page, pageSize });
      const vehicles = res?.data || [];
      setWishlistItems(vehicles.map(toWishlistItem));
      if (res?.pagination) {
        setPagination(res.pagination);
      }
      if (Array.isArray(res?.allVehicleIds)) {
        setSavedVehicleIds(new Set(res.allVehicleIds.map(String)));
      } else if (res?.wishlistItems) {
        setSavedVehicleIds(new Set(res.wishlistItems.map((i: any) => String(i.VehicleID))));
      }
    } catch (error) {
      console.error('Unable to refresh wishlist:', error);
    }
  };

  useEffect(() => {
    const initialiseWishlist = async () => {
      if (isGuest()) {
        setPagination(null);
        setSavedVehicleIds(new Set());
        setWishlistItems(() => {
          try {
            const saved = localStorage.getItem(WISHLIST_STORAGE_KEY);
            return saved ? JSON.parse(saved) : [];
          } catch {
            return [];
          }
        });
        return;
      }

      if (!isBuyer()) {
        setWishlistItems([]);
        setSavedVehicleIds(new Set());
        setPagination(null);
        return;
      }

      try {
        const guestItems = JSON.parse(localStorage.getItem(WISHLIST_STORAGE_KEY) || '[]') as WishlistItem[];
        if (guestItems.length) {
          await wishlistService.syncWishlist(guestItems.map((item) => item.id));
          localStorage.removeItem(WISHLIST_STORAGE_KEY);
        }
        await refreshWishlist();
      } catch (error) {
        console.error('Unable to load wishlist:', error);
      }
    };

    void initialiseWishlist();
    window.addEventListener('morrocars-auth-changed', initialiseWishlist);

    return () => window.removeEventListener('morrocars-auth-changed', initialiseWishlist);
  }, []);

  const isInWishlist = (id: string | number) => {
    const idStr = String(id);
    if (isBuyer() && savedVehicleIds.size > 0) {
      return savedVehicleIds.has(idStr);
    }
    return wishlistItems.some((item) => String(item.id) === idStr);
  };

  const addToWishlist = async (item: WishlistItem) => {
    if (!canUseWishlist()) return;
    if (!isInWishlist(item.id)) {
      if (isBuyer()) {
        setSavedVehicleIds((prev) => new Set([...prev, String(item.id)]));
        await wishlistService.toggleWishlist(item.id);
        await refreshWishlist(pagination?.page_number || 1);
        return;
      }
      const newItem = {
        ...item,
        savedAt: item.savedAt || 'Just now',
      };
      setWishlistItems((prev) => [newItem, ...prev]);
    }
  };

  const removeFromWishlist = async (id: string | number) => {
    if (!canUseWishlist()) return;
    if (isBuyer()) {
      setSavedVehicleIds((prev) => {
        const next = new Set(prev);
        next.delete(String(id));
        return next;
      });
      await wishlistService.toggleWishlist(id);
      await refreshWishlist(pagination?.page_number || 1);
      return;
    }
    setWishlistItems((prev) => prev.filter((item) => String(item.id) !== String(id)));
  };

  const toggleWishlist = async (item: WishlistItem) => {
    if (isInWishlist(item.id)) {
      removeFromWishlist(item.id);
    } else {
      addToWishlist(item);
    }
  };

  const clearWishlist = () => {
    setWishlistItems([]);
    setSavedVehicleIds(new Set());
    setPagination(null);
  };

  const wishlistCount = isBuyer()
    ? (pagination?.total_records ?? savedVehicleIds.size ?? wishlistItems.length)
    : wishlistItems.length;

  return (
    <WishlistContext.Provider
      value={{
        wishlistItems,
        addToWishlist,
        removeFromWishlist,
        toggleWishlist,
        isInWishlist,
        clearWishlist,
        refreshWishlist,
        wishlistCount,
        pagination,
        canUseWishlist: canUseWishlist(),
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
